import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import KuaibaoCard from "@/components/feature/KuaibaoCard";
import { categories, kuaibaoList } from "@/mocks/kuaibao";
import type { KuaibaoItem } from "@/types/kuaibao";

const allItems = kuaibaoList as KuaibaoItem[];
const PAGE_SIZE = 9;

export default function KuaibaoList() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "latest";
  const [keyword, setKeyword] = useState(q);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => setKeyword(q), [q]);
  useEffect(() => setVisible(PAGE_SIZE), [cat, q, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== "") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = [...allItems];
    if (cat !== "all") list = list.filter((item) => item.categorySlug === cat);
    const kw = q.trim().toLowerCase();
    if (kw) {
      list = list.filter((item) =>
        [item.title, item.summary, item.category, item.tags.join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(kw),
      );
    }
    list.sort((a, b) =>
      sort === "heat"
        ? b.heat - a.heat
        : b.publishedAt.localeCompare(a.publishedAt),
    );
    return list;
  }, [cat, q, sort]);

  const currentCat = categories.find((c) => c.slug === cat);
  const heading = currentCat ? `${currentCat.name}快报` : "吃瓜快报";

  const collectionsLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${heading} - 51.cg今日吃瓜单`,
    inLanguage: "zh-CN",
    isPartOf: { "@type": "WebSite", name: "51.cg 今日吃瓜单", url: "https://hizmfjw.cn/" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filtered.slice(0, 20).map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.title,
        url: `https://hizmfjw.cn/kuaibao/${item.slug}`,
      })),
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hizmfjw.cn/" },
      { "@type": "ListItem", position: 2, name: "吃瓜快报", item: "https://hizmfjw.cn/kuaibao" },
    ],
  };

  return (
    <>
      <SeoHead
        title={`${heading}速览 - 51.cg今日吃瓜单`}
        description="51.cg吃瓜快报列表，按分类筛选与关键词搜索，最新与最热两种排序可选。一分钟刷完51.cg全部热点，今日瓜单快报总览一站集齐。"
        keywords="51.cg吃瓜快报,51.cg今日吃瓜单,51.cg一分钟吃瓜,热点快报列表,吃瓜总览"
        path="/kuaibao"
        jsonLd={[collectionsLd, breadcrumbLd]}
      />
      <SiteNavbar />
      <main className="w-full pt-16 md:pt-20">
        <section className="w-full bg-primary-950 pt-8 md:pt-12 pb-10 md:pb-14">
          <div className="w-full px-4 md:px-8">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "吃瓜快报" },
              ]}
            />
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mt-5 mb-3">
              {heading}
            </h1>
            <p className="font-label text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
              {currentCat
                ? `${currentCat.desc}。下面是该分类下的全部快报，可按最新或最热排序浏览。`
                : "全部热点都在这里。支持按分类筛选、关键词搜索，并可在最新与最热之间切换排序，快速找到你关心的那一件事。"}
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 mb-6 md:mb-8">
              <form
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  updateParam("q", keyword.trim());
                }}
                className="flex items-center gap-2 flex-1 max-w-lg"
              >
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-base leading-none"></i>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索标题、话题或标签"
                    className="w-full h-11 pl-10 pr-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 font-label text-sm outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
                <button
                  type="submit"
                  className="h-11 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </form>

              <div className="inline-flex items-center p-1 rounded-full bg-background-100 border border-background-200 self-start lg:self-auto">
                {[
                  { key: "latest", label: "最新" },
                  { key: "heat", label: "最热" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => updateParam("sort", opt.key)}
                    className={`px-4 py-1.5 rounded-full font-label text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      sort === opt.key
                        ? "bg-foreground-900 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6 md:mb-8">
              <button
                type="button"
                onClick={() => updateParam("cat", "all")}
                className={`px-3.5 py-1.5 rounded-full font-label text-sm transition-colors whitespace-nowrap cursor-pointer border ${
                  cat === "all"
                    ? "bg-secondary-500 border-secondary-500 text-background-50"
                    : "bg-background-50 border-background-300 text-foreground-600 hover:border-secondary-400"
                }`}
              >
                全部分类
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => updateParam("cat", c.slug)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-label text-sm transition-colors whitespace-nowrap cursor-pointer border ${
                    cat === c.slug
                      ? "bg-secondary-500 border-secondary-500 text-background-50"
                      : "bg-background-50 border-background-300 text-foreground-600 hover:border-secondary-400"
                  }`}
                >
                  <i className={`${c.icon} leading-none`}></i>
                  {c.name}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4 mb-5 font-label text-sm text-foreground-500">
              <span>
                共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条快报
                {q ? ` · 关键词「${q}」` : ""}
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-3xl leading-none"></i>
                </span>
                <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                  暂时没有匹配的快报
                </h2>
                <p className="font-label text-sm text-foreground-500 mb-5 max-w-md">
                  试试换一个关键词，或者点击下方按钮查看全部热点。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                  className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部快报
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {filtered.slice(0, visible).map((item, i) => (
                    <KuaibaoCard key={item.slug} item={item} index={i} />
                  ))}
                </div>
                {visible < filtered.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 font-label text-sm transition-colors whitespace-nowrap cursor-pointer"
                    >
                      加载更多快报
                      <i className="ri-arrow-down-line leading-none"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}